import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import apiClient from "@/api/client";
import type { LoginRequest, LoginResponse, APIResponse } from "@/types";
import { toastHelpers } from "@/lib/toast-helpers";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { Eye, EyeOff, Store, Users, CreditCard, BarChart3 } from "lucide-react";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LoginRequest>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const user = apiClient.isAuthenticated();
    if (user) {
      // console.log("Already authenticated, redirecting to home...");
      if (user.role === "admin") {
        // console.log("Admin user detected, redirecting to admin panel");
        router.navigate({ to: "/admin/dashboard" });
        return;
      }
    }
  }, []);

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response: APIResponse<LoginResponse> =
        await apiClient.login(credentials);
      return response;
    },
    onSuccess: (response) => {
      // console.log("Login success:", response);
      // console.log("Current API URL:", import.meta.env.VITE_API_URL);
      if (response.success && response.data) {
        apiClient.setAuthToken(response.data.accessToken);

        // Get user info from token for the toast
        const userInfo = apiClient.isAuthenticated();
        const userRole = userInfo?.role;
        const userName = userInfo?.name;

        // Show success toast
        toastHelpers.loginSuccess(userRole, userName);

        router.navigate({ to: "/" });
      } else {
        // console.error("Login failed:", response.message);
        const errorMessage = response.message || t("auth.errors.generic");
        setError(errorMessage);

        // Show login failed toast
        toastHelpers.loginFailed(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error.message || t("auth.errors.generic");
      setError(errorMessage);

      // Show login failed toast
      toastHelpers.loginFailed(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.email || !formData.password) {
      setError(t("auth.errors.required"));
      return;
    }

    loginMutation.mutate(formData);
  };

  // const fillDemoCredentials = (email: string, password: string) => {
  //   setFormData({ email, password });
  // };

  return (
    <div className="h-screen bg-background flex flex-col md:flex-row overflow-hidden">
      {/* Left Panel - Branding */}
      <div className="hidden md:flex w-full md:w-[40%] bg-card border-b md:border-b-0 md:border-r border-border relative overflow-hidden flex-col">
        {/* Branding Content - Centered */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-2 lg:p-3 md:pt-0 lg:pt-0">
          <div className="relative z-10 flex flex-col justify-center items-center max-w-lg w-full">
            {/* Logo */}
            <div className="flex items-center justify-center mb-2 sm:mb-4 md:mb-0">
              <img
                src="/favicon.ico"
                alt="Guestiefy Logo"
                className="w-16 h-16 sm:w-20 sm:h-20 md:w-32 md:h-32 lg:w-48 lg:h-48 xl:w-80 xl:h-80 object-contain rounded-lg"
                onClick={() => window.open("https://smilesiri.com/", "_blank")}
              />
            </div>

            {/* Heading */}
            <h2 className="mb-2 sm:mb-3 md:mb-5 text-center">
              <span className="block text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-foreground leading-tight">
                {t("app.brand.headline")}
              </span>
            </h2>

            {/* Description */}
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-center text-muted-foreground mb-2 sm:mb-3 md:mb-4 lg:mb-6 xl:mb-8 leading-relaxed px-2">
              {t("app.brand.description")}
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4 lg:gap-6 w-full items-start justify-items-center">
              {[
                {
                  icon: Users,
                  title: t("auth.features.staff.title"),
                  desc: t("auth.features.staff.description"),
                },
                {
                  icon: CreditCard,
                  title: t("auth.features.payments.title"),
                  desc: t("auth.features.payments.description"),
                },
                {
                  icon: BarChart3,
                  title: t("auth.features.analytics.title"),
                  desc: t("auth.features.analytics.description"),
                },
                {
                  icon: Store,
                  title: t("auth.features.stock.title"),
                  desc: t("auth.features.stock.description"),
                },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-1.5 lg:gap-2 xl:gap-3"
                >
                  <div className="w-6 h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[10px] lg:text-xs xl:text-sm text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-[9px] lg:text-xs hidden lg:block">
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground)) 1px, transparent 0)",
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Footer - Always at Bottom */}
        <div className="relative z-10 p-3 sm:p-4 md:p-6 border-t border-border/50 bg-card/50 backdrop-blur-sm mt-auto">
          <div className="flex flex-col items-center gap-1.5 lg:gap-2">
            <div
              className="flex flex-col items-center gap-1.5 lg:gap-2 px-3 md:px-4 lg:px-6 py-1 md:py-1.5 lg:py-2 bg-background/80 backdrop-blur-sm border border-border/50 rounded-full shadow-sm hover:bg-background/90 hover:shadow-md transition-all cursor-pointer"
              onClick={() =>
                window.open("https://verveautomation.com/", "_blank")
              }
            >
              <div className="flex items-center gap-1.5 lg:gap-2 min-w-[120px] md:min-w-[140px] lg:min-w-[160px]">
                <div className="w-5 h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 rounded-lg flex items-center justify-center bg-white border border-border">
                  <img
                    src="/VerveAutomation.png"
                    alt="Verve Automation Logo"
                    className="w-4 h-4 md:w-5 md:h-5 lg:w-7 lg:h-7 object-contain"
                  />
                </div>
                <div className="flex flex-col items-start justify-center leading-tight">
                  <span className="text-[9px] md:text-[10px] lg:text-[11px] text-muted-foreground font-medium tracking-wide">
                    {t("app.poweredByUpper")}
                  </span>
                  <span className="text-[10px] md:text-[11px] lg:text-xs font-semibold text-foreground hover:text-primary transition-colors underline decoration-1 underline-offset-2">
                    {t("app.poweredByCta")}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-[9px] md:text-[10px] lg:text-xs text-muted-foreground/70 text-center">
              {t("app.copyright", {
                year: new Date().getFullYear(),
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full md:flex-1 flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8 bg-background overflow-y-auto relative">
        {/* Language Switcher - Top Right */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 md:top-6 md:right-6 lg:top-8 lg:right-8 z-10">
          <LanguageSwitcher variant="minimal" size="default" />
        </div>

        <div className="w-full max-w-md">
          <Card className="shadow-lg border border-border">
            <CardHeader className="text-center items-center pb-4 md:pb-6">
              {/* <CardTitle className="text-lg md:text-xl xl:text-2xl font-bold text-foreground">
                {t("auth.loginTitle")}
              </CardTitle> */}
              <img
                src="/favicon.ico"
                alt="Guestiefy Logo"
                className="w-20 md:w-24 md:h-24 lg:w-32 h-20 lg:h-32 object-contain rounded-lg"
              />
              <CardDescription className="text-xs md:text-sm xl:text-base text-muted-foreground">
                {t("auth.loginDescription")}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 md:space-y-5">
              <form
                onSubmit={handleSubmit}
                className="space-y-2.5 md:space-y-3"
              >
                <div className="space-y-1 md:space-y-1.5">
                  <label className="text-xs md:text-sm font-medium text-foreground">
                    {t("auth.emailLabel")}
                  </label>
                  <Input
                    type="text"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="h-9 md:h-10 text-sm"
                    autoComplete="email"
                    disabled={loginMutation.isPending}
                  />
                </div>

                <div className="space-y-1 md:space-y-1.5">
                  <label className="text-xs md:text-sm font-medium text-foreground">
                    {t("auth.passwordLabel")}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      className="h-9 md:h-10 pr-10 text-sm"
                      autoComplete="current-password"
                      disabled={loginMutation.isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-destructive/10 border border-destructive/20 text-destructive p-3 md:p-4 rounded-lg text-xs md:text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-destructive rounded-full flex items-center justify-center flex-shrink-0">
                        <div className="w-1.5 h-1.5 bg-destructive-foreground rounded-full"></div>
                      </div>
                      <span className="font-medium">
                        {t("auth.errorHeading")}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-destructive/80">
                      {error}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-9 md:h-10 text-xs md:text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-primary-foreground/20 border-t-primary-foreground rounded-full animate-spin" />
                      {t("auth.submit.signingIn")}
                    </div>
                  ) : (
                    t("auth.submit.signIn")
                  )}
                </Button>
              </form>
              {/* Demo Accounts */}
              {/* <div className="border-t border-border pt-3 md:pt-4">
                <div>
                  <div className="text-[10px] md:text-xs text-muted-foreground mb-2 font-medium">
                    {t("auth.demo.title")}
                  </div>
                  <div className="grid gap-1.5 md:gap-2">
                    {[
                      {
                        email: "admin@dentfull.com",
                        role: t("auth.demo.admin.role"),
                        icon: Settings,
                        bg: "bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/15",
                        desc: t("auth.demo.admin.description"),
                        password: "smile@123",
                      },
                      {
                        email: "manager@dentfull.com",
                        role: t("auth.demo.manager.role"),
                        icon: BarChart3,
                        bg: "bg-primary/10 text-primary border-primary/20 hover:bg-primary/15",
                        desc: t("auth.demo.manager.description"),
                        password: "manager123",
                      },
                      {
                        email: "counter@dentfull.com",
                        role: t("auth.demo.counter.role"),
                        icon: CreditCard,
                        bg: "bg-green-500/10 text-green-700 border-green-500/20 hover:bg-green-500/15",
                        desc: t("auth.demo.counter.description"),
                        password: "counter123",
                      },
                    ].map((account) => (
                      <button
                        key={account.email}
                        onClick={() =>
                          fillDemoCredentials(account.email, account.password)
                        }
                        className={`flex items-center justify-between p-2 md:p-3 border rounded-lg ${account.bg} text-left transition-all duration-200 hover:shadow-sm`}
                        disabled={loginMutation.isPending}
                      >
                        <div className="flex items-center gap-2 md:gap-2.5">
                          <div className="w-6 h-6 md:w-7 md:h-7 bg-card rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                            <account.icon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                          </div>
                          <div>
                            <div className="font-semibold text-xs md:text-sm">
                              {account.role}
                            </div>
                            <div className="text-[10px] md:text-xs opacity-75 mt-0.5">
                              {account.desc}
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] md:text-xs opacity-60 font-mono bg-card px-1.5 py-0.5 rounded flex-shrink-0">
                          {account.password}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div> */}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
